import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { AuthShell, Field } from "@/pages/Login";

export default function ConfirmEmail() {
  const { confirm, resendConfirmation, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/console", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await confirm(email.trim(), code.trim(), password);
      navigate("/console", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    setError("");
    setInfo("");
    try {
      await resendConfirmation(email.trim());
      setInfo("A new verification code was sent.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AuthShell>
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <span className="chip-brand" data-testid="confirm-chip">
          / 01b · verify email
        </span>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold text-white leading-[0.95] tracking-tighter">
          Enter your code.
        </h1>
        <p className="mt-4 text-white/50 text-sm font-light leading-relaxed">
          We sent a verification code to your inbox (check spam). Enter it below with your
          password to finish signup.
        </p>

        <div className="mt-10 space-y-5">
          <Field
            id="email"
            testId="confirm-email"
            label="Email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={setEmail}
          />
          <Field
            id="code"
            testId="confirm-code"
            label="Verification code"
            required
            autoComplete="one-time-code"
            value={code}
            onChange={setCode}
          />
          <Field
            id="password"
            testId="confirm-password"
            label="Password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
        </div>

        {error && (
          <div
            data-testid="confirm-error"
            className="mt-5 border border-mclaren/40 bg-mclaren/5 px-4 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-mclaren"
          >
            {error}
          </div>
        )}
        {info && (
          <div className="mt-5 border border-cyan-electric/30 bg-cyan-electric/5 px-4 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-cyan-electric">
            {info}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="confirm-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 h-12 px-6 bg-mclaren text-void font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-sunset transition-all disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying
            </>
          ) : (
            <>
              Verify & continue <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </>
          )}
        </button>

        <button
          type="button"
          onClick={onResend}
          data-testid="confirm-resend"
          className="mt-4 w-full font-mono-tech text-[11px] uppercase tracking-widest text-cyan-electric hover:text-white"
        >
          Resend code
        </button>

        <p className="mt-6 text-white/50 text-sm">
          Back to{" "}
          <Link to="/signup" className="text-cyan-electric underline underline-offset-4">
            signup
          </Link>
        </p>
      </motion.form>
    </AuthShell>
  );
}
